import { useEffect, useState } from 'react';
import api from '../api';

const ONE_DAY = 1000 * 60 * 60 * 24;

function convertDate(dateObject) {
  const year  = dateObject.getFullYear();
  const month = String(dateObject.getMonth() + 1).padStart(2, '0');
  const day   = String(dateObject.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function mapServerData(rawData) {
  return rawData.map(item => ({
    date:             new Date(item.time),
    precipitation:    item.PRECTOTCORR_Average,
    precipitationMin: item.PRECTOTCORR_Min,
    precipitationMax: item.PRECTOTCORR_Max,
    temperature:      item.T2M_Average,
    temperatureMin:   item.T2M_Min,
    temperatureMax:   item.T2M_Max,
    windSpeed:        item.WS2M_Average,
    windSpeedMin:     item.WS2M_Min,
    windSpeedMax:     item.WS2M_Max,
    windDirection:    item.WD2M_Average,
    windDirectionMin: item.WD2M_Min,
    windDirectionMax: item.WD2M_Max,
    watts:            item.ALLSKY_SFC_SW_DWN_Average,
    wattsMin:         item.ALLSKY_SFC_SW_DWN_Min,
    wattsMax:         item.ALLSKY_SFC_SW_DWN_Max,
    cloud:            item.CLOUD_AMT_Average,
    cloudMin:         item.CLOUD_AMT_Min,
    cloudMax:         item.CLOUD_AMT_Max,
    rh2m:             item.RH2M_Average,
    rh2mMin:          item.RH2M_Min,
    rh2mMax:          item.RH2M_Max,
  }));
}

/**
 * A custom hook to fetch forecast data via axios
 * @param {string} userId
 * @param {Date}   startDate
 * @param {Date}   endDate
 * @param {Object} unitSettings
 */
export function useForecastData(userId, startDate, endDate, unitSettings) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!userId || !startDate) {
      setLoading(false);
      return;
    }

    const finalEnd = endDate ?? new Date(startDate.getTime() + ONE_DAY);
    const spanMs   = finalEnd.getTime() - startDate.getTime();
    const scale    = spanMs <= ONE_DAY ? 'hourly' : 'daily';

    const params = {
      user_id:            userId,
      start_time:         convertDate(startDate),
      end_time:           convertDate(finalEnd),
      scale,
      t2m_units:          unitSettings.t2m_units,
      precipitation_units: unitSettings.precipitation_units,
      ws2m_units:         unitSettings.ws2m_units,
    };

    setLoading(true);
    api.get('/get_dashboard_data/', { params })
      .then(res => {
        if (!res.data.rv) {
          throw new Error('Response missing `rv` payload');
        }
        const mapped = mapServerData(res.data.rv);
        mapped.sort((a, b) => a.date - b.date);
        setData(mapped);
      })
      .catch(err => {
        console.error('Error fetching forecast data:', err);
        setError(err);
      })
      .finally(() => setLoading(false));

  }, [userId, startDate, endDate, unitSettings]);

  return { data, loading, error };
}